import React, { useEffect } from 'react';
import { ArrowLeft, Shield } from 'lucide-react';

interface PoliticaPrivacidadProps {
  navigateToPage: (page: string) => void;
}

export default function PoliticaPrivacidad({ navigateToPage }: PoliticaPrivacidadProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
        {/* Cabecera / Banner superior */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-8 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white"></div>
            <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-white"></div>
          </div>
          
          <button
            onClick={() => navigateToPage('home')}
            className="inline-flex items-center text-blue-100 hover:text-white mb-6 transition-colors group focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-700 rounded-lg p-1"
            aria-label="Volver a la página de inicio"
          >
            <ArrowLeft className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform" />
            Volver al inicio
          </button>
          
          <div className="flex items-center gap-4">
            <div className="bg-white/10 p-3 rounded-xl backdrop-blur-sm">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Política de Privacidad</h1>
              <p className="text-blue-100 text-sm mt-1">Cumplimiento del RGPD y la LOPD-GDD</p>
            </div>
          </div>
        </div>

        {/* Contenido Legal */}
        <div className="p-8 sm:p-10 space-y-8 text-gray-700 leading-relaxed">
          <section className="border-b border-gray-100 pb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">1. Identificación del Responsable del Tratamiento</h2>
            <p className="mb-2">
              En cumplimiento del Reglamento General de Protección de Datos (RGPD) y la Ley Orgánica 3/2018, de 5 de diciembre, de Protección de Datos Personales y garantía de los derechos digitales (LOPD-GDD), le informamos que el responsable del tratamiento de sus datos personales es:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1 bg-gray-50 p-4 rounded-xl border border-gray-100">
              <li><strong>Titular:</strong> José Luis Construcciones</li>
              <li><strong>CIF / NIF:</strong> B-XXXXXXXX</li>
              <li><strong>Domicilio Social:</strong> C. Ing. García Pimentel, 37, 23600 Martos, Jaén</li>
              <li><strong>Teléfono de contacto:</strong> 953 03 95 67 / 650 44 13 96</li>
              <li><strong>Correo electrónico:</strong> construccionesperezrosa@gmail.com</li>
              <li><strong>Sitio Web:</strong> joseluisconstrucciones.com</li>
            </ul>
          </section>

          <section className="border-b border-gray-100 pb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">2. Datos Personales que Recopilamos</h2>
            <p>
              Únicamente recopilamos los datos personales que usted nos proporciona voluntariamente a través del formulario de contacto de nuestro sitio web o por correo electrónico. Estos datos incluyen:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Nombre y apellidos</li>
              <li>Dirección de correo electrónico</li>
              <li>Número de teléfono</li>
              <li>Cualquier información que decida incluir en el mensaje o consulta</li>
            </ul>
          </section>

          <section className="border-b border-gray-100 pb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">3. Finalidad del Tratamiento</h2>
            <p>
              Tratamos sus datos personales con las siguientes finalidades:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Responder a sus consultas, solicitudes de información o presupuestos realizados a través de la web.</li>
              <li>Gestionar la relación comercial y la prestación de los servicios de reformas, albañilería, fontanería, electricidad o afines que contrate con nosotros.</li>
              <li>Cumplir con las obligaciones legales aplicables a nuestra actividad empresarial.</li>
            </ul>
            <p className="mt-2 text-sm text-gray-500 italic">
              * No realizamos tratamientos automatizados de datos ni elaboración de perfiles con fines publicitarios sin su consentimiento explícito previo.
            </p>
          </section>

          <section className="border-b border-gray-100 pb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">4. Legitimación para el Tratamiento</h2>
            <p>
              La base legal que legitima el tratamiento de sus datos es:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>El <strong>consentimiento del interesado</strong> (Art. 6.1.a del RGPD) al rellenar y enviar el formulario de contacto o enviarnos un correo electrónico.</li>
              <li>La <strong>ejecución de un contrato</strong> o aplicación de medidas precontractuales (Art. 6.1.b del RGPD) en caso de que solicite la elaboración de un presupuesto o contrate nuestros servicios de construcción y reformas.</li>
            </ul>
          </section>

          <section className="border-b border-gray-100 pb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">5. Conservación de los Datos</h2>
            <p>
              Los datos personales proporcionados se conservarán durante el tiempo estrictamente necesario para cumplir con la finalidad para la que fueron recabados y para determinar las posibles responsabilidades que se pudieran derivar de dicha finalidad y de la relación comercial, además de los periodos establecidos por la normativa tributaria y sectorial vigente.
            </p>
          </section>

          <section className="border-b border-gray-100 pb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">6. Destinatarios de los Datos</h2>
            <p>
              Sus datos no serán vendidos, alquilados ni cedidos a terceros bajo ningún concepto, salvo obligación legal expresa o cuando sea estrictamente necesario para la prestación del servicio contratado (por ejemplo, a subcontratistas autorizados o entidades financieras implicadas en el cobro). No se realizan transferencias internacionales de datos fuera del Espacio Económico Europeo.
            </p>
          </section>

          <section className="border-b border-gray-100 pb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">7. Sus Derechos en Materia de Protección de Datos</h2>
            <p>
              Como titular de los datos, el RGPD y la LOPD-GDD le otorgan el derecho a controlar la información personal que disponemos sobre usted. Puede ejercer en cualquier momento de forma gratuita los siguientes derechos:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-2">
              <li><strong>Derecho de Acceso:</strong> Conocer qué datos tratamos sobre usted y obtener una copia de los mismos.</li>
              <li><strong>Derecho de Rectificación:</strong> Solicitar la corrección de datos inexactos o incompletos.</li>
              <li><strong>Derecho de Supresión (Derecho al Olvido):</strong> Solicitar la eliminación de sus datos cuando ya no sean necesarios para los fines que fueron recabados.</li>
              <li><strong>Derecho de Oposición:</strong> Oponerse al tratamiento de sus datos en determinadas circunstancias.</li>
              <li><strong>Derecho a la Limitación del Tratamiento:</strong> Solicitar que suspendamos temporalmente el tratamiento de sus datos.</li>
              <li><strong>Derecho a la Portabilidad:</strong> Recibir sus datos en un formato electrónico estructurado y de uso común para transmitirlos a otro responsable.</li>
              <li><strong>Retirada del Consentimiento:</strong> Retirar el consentimiento otorgado en cualquier momento, sin que ello afecte a la licitud del tratamiento previo a su retirada.</li>
            </ul>
            <p className="mt-4">
              Para ejercer estos derechos, debe enviar una solicitud por escrito a la dirección física indicada arriba o bien por correo electrónico a <strong>construccionesperezrosa@gmail.com</strong>, acreditando debidamente su identidad (adjuntando copia de su DNI o documento equivalente).
            </p>
            <p className="mt-2">
              Asimismo, si considera que sus derechos no han sido atendidos correctamente, tiene derecho a presentar una reclamación ante la <strong>Agencia Española de Protección de Datos (AEPD)</strong> a través de su sede electrónica (www.aepd.es).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">8. Medidas de Seguridad</h2>
            <p>
              José Luis Construcciones ha implementado las medidas de seguridad técnicas y organizativas necesarias para evitar la pérdida, alteración, robo o tratamiento no autorizado de sus datos personales, teniendo en cuenta el estado de la tecnología, la naturaleza de los datos almacenados y los riesgos a los que están expuestos, garantizando la confidencialidad, integridad y disponibilidad de la información.
            </p>
          </section>
        </div>

        {/* Footer interior de la página */}
        <div className="bg-gray-50 px-8 py-6 border-t border-gray-100 flex justify-between items-center flex-wrap gap-4 text-sm text-gray-500">
          <span>Última actualización: Julio de 2026</span>
          <button
            onClick={() => navigateToPage('home')}
            className="text-blue-600 hover:text-blue-800 font-semibold underline focus:outline-none"
          >
            Volver al Inicio
          </button>
        </div>
      </div>
    </div>
  );
}
